<script setup lang="ts">
import type { Collections } from '@nuxt/content'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

const { locale, t } = useI18n()
const localePath = useLocalePath()

const { data: projects } = await useAsyncData('projects', async () => {
  const collection = ('projects_' + locale.value) as keyof Collections
  return await queryCollection(collection).all() as Collections['projects_en'][] | Collections['projects_fr'][]
}, {
  watch: [locale],
})

const items = computed(() => (projects.value || [])
  .filter(project => project.featured)
  .map((project: any) => ({
    name: project.name,
    to: localePath('/projects/' + project.stem.split('/').pop()),
    phone: project.body?.phone ?? project.image,
    type: project.industry,
    year: /soon|bient/i.test(project.release || '') ? t('global.soon') : project.release,
    summary: project.summary,
  })))

const pad = (value: number) => String(value).padStart(2, '0')

const root = ref<HTMLElement>()
let media: gsap.MatchMedia | undefined

function pinnedTimeline(q: gsap.utils.SelectorFunc) {
  const layers = q('.pj-layer')
  const images = q('.pj-layer img')
  const names = q('.pj-name')
  const metas = q('.pj-meta')
  const bars = q('.pj-bar')

  const timeline = gsap.timeline({
    defaults: { ease: 'none', duration: 1 },
    scrollTrigger: {
      trigger: q('.pj-track')[0],
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.6,
    },
  })

  // each step is followed by a short hold, so a project can be read before the next one
  for (let index = 1; index < layers.length; index++) {
    const at = 0.25 + (index - 1) * 1.4
    timeline
      .fromTo(layers[index], { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', ease: 'power2.inOut' }, at)
      .fromTo(images[index], { scale: 1.2 }, { scale: 1, ease: 'power2.out' }, at)
      .to(images[index - 1], { scale: 1.08 }, at)
      .to(names[index - 1], { opacity: 0.25, x: 0, ease: 'power2.inOut' }, at)
      .to(names[index], { opacity: 1, x: 16, ease: 'power2.inOut' }, at)
      .fromTo(bars[index], { scaleX: 0 }, { scaleX: 1, ease: 'power2.inOut' }, at)
      .to(metas[index - 1], { autoAlpha: 0, y: -12, duration: 0.4 }, at)
      .fromTo(metas[index], { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: 0.4 }, at + 0.6)
      .to(q('.pj-digits')[0], { yPercent: -(100 / layers.length) * index, ease: 'power2.inOut' }, at)
  }
  timeline.to({}, { duration: 0.25 })
}

function stackReveal(q: gsap.utils.SelectorFunc) {
  q('.pj-card').forEach((card) => {
    gsap.timeline({ scrollTrigger: { trigger: card, start: 'top 85%', once: true } })
      .from(card.querySelector('.pj-shot'), { clipPath: 'inset(100% 0% 0% 0%)', duration: 0.9, ease: 'power3.out', clearProps: 'clipPath' })
      .from(card.querySelector('img'), { scale: 1.2, duration: 1.1, ease: 'power3.out' }, 0)
      .from(card.querySelector('.pj-text'), { y: 16, opacity: 0, duration: 0.6, ease: 'power2.out' }, 0.3)
  })
}

onMounted(() => {
  gsap.registerPlugin(ScrollTrigger)
  media = gsap.matchMedia()
  const q = gsap.utils.selector(root.value)
  media.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => pinnedTimeline(q), root.value)
  media.add('(max-width: 1023px) and (prefers-reduced-motion: no-preference)', () => stackReveal(q), root.value)
})

onBeforeUnmount(() => media?.revert())
</script>

<template>
  <div
    ref="root"
    class="w-full"
  >
    <!-- large screens: the section stays on screen while the projects follow one another -->
    <div
      class="pj-track pj-pinned relative"
      :style="{ height: `calc(100vh + ${(items.length - 1) * 70}vh)` }"
    >
      <div class="sticky top-0 flex h-screen flex-col justify-between pb-10 pt-20">
        <div>
          <div class="flex items-end justify-between">
            <h2 class="text-5xl font-semibold leading-none tracking-tight text-white xl:text-6xl">
              {{ $t("navigation.works") }}
            </h2>
            <p class="flex items-baseline gap-2 text-lg tabular-nums text-white/40">
              <span class="inline-block h-[1em] overflow-hidden text-5xl font-semibold leading-none tracking-tight text-white xl:text-6xl">
                <span class="pj-digits block">
                  <span
                    v-for="(_, index) in items"
                    :key="index"
                    class="block"
                  >{{ pad(index + 1) }}</span>
                </span>
              </span>
              / {{ pad(items.length) }}
            </p>
          </div>
          <!-- one segment per project, filled as the page scrolls -->
          <div class="mt-5 flex gap-2">
            <span
              v-for="(project, index) in items"
              :key="project.to"
              class="h-0.5 flex-1 overflow-hidden rounded-full bg-white/15"
            >
              <span
                class="pj-bar block size-full origin-left bg-white"
                :style="index > 0 ? { transform: 'scaleX(0)' } : undefined"
              />
            </span>
          </div>
        </div>

        <div class="mt-8 flex min-h-0 flex-1 gap-8">
          <div class="flex w-3/5 flex-col justify-between">
            <!-- names -->
            <ul class="my-auto flex flex-col gap-3">
              <li
                v-for="(project, index) in items"
                :key="project.to"
              >
                <NuxtLink
                  :to="project.to"
                  class="pj-name block whitespace-nowrap text-5xl font-semibold leading-[1.1] tracking-tight text-white xl:text-7xl"
                  :style="index === 0 ? { transform: 'translateX(16px)' } : { opacity: 0.25 }"
                >
                  {{ project.name }}
                </NuxtLink>
              </li>
            </ul>

            <div class="flex items-end justify-between gap-8">
              <div class="relative h-16 flex-1">
                <div
                  v-for="(project, index) in items"
                  :key="project.to"
                  class="pj-meta absolute inset-x-0 bottom-0"
                  :style="index > 0 ? { visibility: 'hidden', opacity: 0 } : undefined"
                >
                  <p class="text-sm text-white">
                    {{ project.type }} · {{ project.year }}
                  </p>
                  <p class="mt-1 text-pretty text-sm text-white/60">
                    {{ project.summary }}
                  </p>
                </div>
              </div>
              <NuxtLinkLocale
                to="/works"
                class="group flex shrink-0 items-center gap-2 text-sm font-medium text-white/70 transition-colors hover:text-white"
              >
                {{ $t("global.all_projects") }}
                <UIcon
                  name="heroicons:arrow-right"
                  class="size-4 transition-transform duration-300 group-hover:translate-x-1"
                />
              </NuxtLinkLocale>
            </div>
          </div>

          <!-- the phone: only its screen changes while scrolling -->
          <div class="relative w-2/5">
            <div class="absolute inset-y-0 left-1/2 aspect-[390/844] -translate-x-1/2">
              <ProjectPhone class="size-full">
                <NuxtLink
                  v-for="(project, index) in items"
                  :key="project.to"
                  :to="project.to"
                  tabindex="-1"
                  aria-hidden="true"
                  class="pj-layer absolute inset-0 block overflow-hidden"
                  :style="index > 0 ? { clipPath: 'inset(100% 0% 0% 0%)' } : undefined"
                >
                  <NuxtImg
                    :src="project.phone"
                    width="780"
                    densities="x1"
                    alt=""
                    class="size-full object-cover object-top"
                  />
                </NuxtLink>
              </ProjectPhone>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- small screens, or reduced motion: a plain list -->
    <div class="pj-stack mx-auto flex max-w-4xl flex-col gap-12">
      <h2 class="text-4xl font-semibold tracking-tight text-white sm:text-5xl">
        {{ $t("navigation.works") }}
      </h2>
      <NuxtLink
        v-for="project in items"
        :key="project.to"
        :to="project.to"
        class="pj-card block"
      >
        <ProjectPhone class="pj-shot mx-auto w-[min(70vw,17rem)]">
          <NuxtImg
            :src="project.phone"
            width="640"
            densities="x1"
            loading="lazy"
            :alt="project.name"
            class="size-full object-cover object-top"
          />
        </ProjectPhone>
        <span class="pj-text mt-5 block text-center">
          <span class="block text-3xl font-semibold tracking-tight text-white sm:text-4xl">{{ project.name }}</span>
          <span class="mt-1 block text-sm text-white/60">{{ project.type }} · {{ project.year }}</span>
        </span>
      </NuxtLink>
      <NuxtLinkLocale
        to="/works"
        class="flex items-center gap-2 text-sm font-medium text-white/70"
      >
        {{ $t("global.all_projects") }}
        <UIcon
          name="heroicons:arrow-right"
          class="size-4"
        />
      </NuxtLinkLocale>
    </div>
  </div>
</template>

<style scoped>
  .pj-pinned {
    display: none;
  }

  @media (min-width: 1024px) and (prefers-reduced-motion: no-preference) {
    .pj-pinned {
      display: block;
    }

    .pj-stack {
      display: none;
    }
  }
</style>
